import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Leaf, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/apiClient";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative z-10 min-h-screen bg-forest-dark flex items-center justify-center px-6">
      <div className="absolute top-6 left-6">
        <button
          onClick={() => navigate("/")}
          data-testid="back-home-btn"
          className="flex items-center gap-2 text-white/70 hover:text-white text-sm"
        >
          <ArrowLeft className="h-4 w-4" /> Back to site
        </button>
      </div>
      <div className="w-full max-w-md bg-bone rounded-[2rem] p-10 border border-stone-200">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-lg bg-forest flex items-center justify-center">
            <Leaf className="h-5 w-5 text-white" />
          </div>
          <span className="font-serif text-2xl text-forest-dark">Paryavaran Suraksha Admin</span>
        </div>
        <h1 className="text-3xl font-serif text-forest-dark mb-1">Welcome back</h1>
        <p className="text-sm text-muted-foreground mb-8">Sign in to manage donations and activities.</p>

        <form onSubmit={submit} className="space-y-5" data-testid="login-form">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-forest-dark">Email</Label>
            <Input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              className="rounded-xl border-stone-300 bg-white focus-visible:ring-forest"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-forest-dark">Password</Label>
            <Input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="rounded-xl border-stone-300 bg-white focus-visible:ring-forest"
            />
          </div>
          {error && (
            <p data-testid="login-error" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full rounded-full bg-forest hover:bg-forest-dark text-white py-6 transition-transform hover:-translate-y-0.5"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
